/**
 * Render Orchestrator - manages main render() function and related helpers
 */

import type { Lot } from '../../types';
import * as config from '../modules/config';
import { els } from '../modules/ui';
import { ensureSettings, SORT_FIELDS } from '../modules/state';
import { sanitizePercentage, sanitizeCurrency } from '../modules/formatting';
import { sortLots, applyLotFilters } from '../modules/lotOperations';
import { triggerDistanceCalculations } from '../modules/distance';
import { buildTableRow } from '../modules/tableRenderer';
import {
  groupLotsByAuction,
  computeGroupRollup,
  buildAuctionGroupHeaderRow,
  isGroupCollapsed,
} from '../modules/auctionGrouping';
import { updateBudgetWidget } from '../modules/budget';
import { buildLotCardHtml } from '../modules/rendering';
import { getEffectiveCloseTime, hasCustomCloseTime } from '../modules/storage';
import {
  updateRemoveClosedLotsButton,
  updateRemoveDeadMeatButton,
  updateArchiveUrlsButton,
} from '../renderHelpers';
import { updateViewModeToggle as updateViewModeToggleHelper } from '../uiHelpers';

/**
 * Render state tracking to minimize DOM updates
 */
const lastRenderState: any = {
  rows: '',
  pageInfo: '',
  prevDisabled: null,
  nextDisabled: null,
  closeSortActive: null,
  closeSortDirection: '',
  closeSortAria: '',
  viewModeHidden: null,
  viewModeLabel: '',
  viewModeAria: '',
  viewModeActive: '',
};

/**
 * Set table rows HTML (only if changed)
 */
function setRowsHtml(html: string) {
  if (!els.rows) return;
  if (lastRenderState.rows !== html) {
    els.rows.innerHTML = html;
    lastRenderState.rows = html;
  }
}

/**
 * Set page info text (only if changed)
 */
function setPageInfo(text: string) {
  if (!els.pageInfo) return;
  if (lastRenderState.pageInfo !== text) {
    els.pageInfo.textContent = text;
    lastRenderState.pageInfo = text;
  }
}

/**
 * Set previous button disabled state (only if changed)
 */
function setPrevDisabled(value: boolean) {
  if (!els.prev) return;
  if (lastRenderState.prevDisabled !== value) {
    (els.prev as HTMLButtonElement).disabled = value;
    lastRenderState.prevDisabled = value;
  }
}

/**
 * Set next button disabled state (only if changed)
 */
function setNextDisabled(value: boolean) {
  if (!els.next) return;
  if (lastRenderState.nextDisabled !== value) {
    (els.next as HTMLButtonElement).disabled = value;
    lastRenderState.nextDisabled = value;
  }
}

/**
 * Update close sort button state
 */
export function updateCloseSortButton(state: any) {
  if (!els.sortClose) return;
  const isActive = state.sort && state.sort.field === SORT_FIELDS.CLOSE;
  const rawDirection = isActive
    ? state.sort && state.sort.direction === 'asc'
      ? 'asc'
      : 'desc'
    : '';
  const displayDirection = rawDirection; // Show current direction, not inverted
  const ariaLabel = isActive
    ? `Sort by close date, current order ${rawDirection === 'asc' ? 'ascending' : 'descending'}.`
    : 'Sort by close date';
  const pressedValue = isActive ? 'true' : 'false';
  if (lastRenderState.closeSortActive !== isActive) {
    els.sortClose.setAttribute('data-active', String(isActive));
    els.sortClose.setAttribute('aria-pressed', pressedValue);
    lastRenderState.closeSortActive = isActive;
  }
  if (lastRenderState.closeSortDirection !== displayDirection) {
    if (displayDirection) {
      els.sortClose.setAttribute('data-direction', displayDirection);
    } else {
      els.sortClose.removeAttribute('data-direction');
    }
    lastRenderState.closeSortDirection = displayDirection;
  }
  if (lastRenderState.closeSortAria !== ariaLabel) {
    els.sortClose.setAttribute('aria-label', ariaLabel);
    els.sortClose.setAttribute('title', ariaLabel);
    lastRenderState.closeSortAria = ariaLabel;
  }
}

/**
 * Update view mode toggle button
 */
export function updateViewModeToggle(state: any) {
  updateViewModeToggleHelper(state, els, lastRenderState);
}

/**
 * Main render function - updates the entire UI based on current state
 */
export function createRenderFunction(state: any) {
  return function render() {
    if (!state) return; // Guard against early calls before state initialization

    updateViewModeToggle(state);

    if (els.profileCompany) {
      const disabled = state.mode !== 'auth0';
      (els.profileCompany as HTMLInputElement).disabled = disabled;
      if (disabled) {
        els.profileCompany.setAttribute('title', 'Sign in to update your profile');
      } else {
        els.profileCompany.removeAttribute('title');
      }
    }

    const hasLots = Array.isArray(state.lots) && state.lots.length > 0;

    // Update button states using render helpers
    updateRemoveClosedLotsButton(els.removeClosedLots, state.lots, !!state.removingClosedLots);
    updateRemoveDeadMeatButton(els.removeDeadMeat, state.lots, !!state.removingDeadMeat);
    updateArchiveUrlsButton(els.archiveUrls, state.viewMode);
    updateBudgetWidget(els.budgetWidget as HTMLElement | null, state.lots, state.settings);

    updateCloseSortButton(state);

    const settings = ensureSettings();
    const taxRate = sanitizePercentage(settings && settings.taxRate, 0);
    const bidderPremium = sanitizePercentage(settings && settings.bidderPremium, 15);
    const sellerCosts = sanitizePercentage(settings && settings.sellerCosts, 14);
    const fixedLotCost = sanitizeCurrency(settings && settings.fixedLotCost, 0);
    settings.taxRate = taxRate;
    settings.bidderPremium = bidderPremium;
    settings.sellerCosts = sellerCosts;
    settings.fixedLotCost = fixedLotCost;

    if (state.isFetchingRemote && !hasLots) {
      setRowsHtml(
        `<tr><td colspan="${config.TABLE_COLUMN_COUNT}" class="table-empty-state">Loading lots…</td></tr>`
      );
      setPageInfo('Loading...');
      setPrevDisabled(true);
      setNextDisabled(true);
      return;
    }

    const pageSize = state.pageSize;
    const data = applyLotFilters(sortLots(state.lots.slice()));
    const total = data.length;
    const pages = Math.max(1, Math.ceil(total / pageSize));
    if (state.page > pages) state.page = pages;
    if (state.page < 1) state.page = 1;
    const start = (state.page - 1) * pageSize;
    const pageData = data.slice(start, start + pageSize);

    const homeZip = state.settings && state.settings.zipCode ? state.settings.zipCode : '';
    if (homeZip) {
      triggerDistanceCalculations(pageData, homeZip, state.settings);
    }

    let rowsHtml;
    if (pageData.length) {
      const buildRow = (lot: Lot) =>
        buildTableRow(lot, state, settings, getEffectiveCloseTime, (id?: string | null) =>
          hasCustomCloseTime(id || '')
        );

      if (settings.groupByAuction) {
        // Collapsible per-auction sections with decision rollups.
        const groups = groupLotsByAuction(pageData);
        const parts: string[] = [];
        for (const group of groups) {
          const rollup = computeGroupRollup(group.lots, settings, (lot: Lot) => {
            const value = getEffectiveCloseTime(lot);
            return typeof value === 'number' ? value : null;
          });
          const collapsed = isGroupCollapsed(group.key);
          parts.push(
            buildAuctionGroupHeaderRow(group, rollup, collapsed, config.TABLE_COLUMN_COUNT)
          );
          if (!collapsed) {
            for (const lot of group.lots) parts.push(buildRow(lot));
          }
        }
        rowsHtml = parts.join('');
      } else {
        rowsHtml = pageData.map(buildRow).join('');
      }
    } else {
      rowsHtml = `<tr><td colspan="${config.TABLE_COLUMN_COUNT}" class="table-empty-state">No lots found.</td></tr>`;
    }
    setRowsHtml(rowsHtml);

    if (els.lotCardsContainer) {
      els.lotCardsContainer.innerHTML = pageData.length
        ? pageData.map(buildLotCardHtml).join('')
        : '';
    }

    setPageInfo(`Page ${state.page} of ${pages} — ${total} result${total === 1 ? '' : 's'}`);
    setPrevDisabled(state.page <= 1);
    setNextDisabled(state.page >= pages);
  };
}

/**
 * Export last render state for use in other modules
 */
export function getLastRenderState() {
  return lastRenderState;
}
